import React, { useRef, useEffect, useState } from "react";
import { animateSocialIcon, createLogoFloatingAnimation } from "./gsap-footer";
import {
  BUSINESS_NAME,
  BUSINESS_DESCRIPTION,
  BUSINESS_SOCIAL_MEDIA,
  getSocialIcon,
} from "@/core/constants/business";
import gsap from "gsap";

const CompanyInfo = () => {
  const socialIconRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const logoRef = useRef<HTMLImageElement>(null);

  // Image loading states following the standardized pattern
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Use centralized business social media data
  const socialMediaLinks = [
    {
      platform: "instagram",
      url:
        BUSINESS_SOCIAL_MEDIA.instagram ||
        "https://instagram.com/streetlawyerservices",
      label: "Follow us on Instagram",
    },
    {
      platform: "youtube",
      url:
        BUSINESS_SOCIAL_MEDIA.youtube ||
        "https://youtube.com/@streetlawyerservices",
      label: "Subscribe to our YouTube",
    },
    {
      platform: "facebook",
      url:
        BUSINESS_SOCIAL_MEDIA.facebook ||
        "https://facebook.com/streetlawyerservices",
      label: "Like us on Facebook",
    },
    {
      platform: "tiktok",
      url:
        BUSINESS_SOCIAL_MEDIA.tiktok ||
        "https://tiktok.com/@streetlawyerservices",
      label: "Follow us on TikTok",
    },
  ];

  // Image load handler following the standardized pattern
  const handleLogoLoad = (img: HTMLImageElement) => {
    console.log("✅ Footer logo loaded successfully");
    setLogoLoaded(true);

    // GSAP fade-in animation
    gsap.to(img, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.out",
    });
  };

  // Image error handler
  const handleLogoError = () => {
    console.error("❌ Failed to load footer logo");
    setLogoError(true);
  };

  useEffect(() => {
    const cleanupFunctions: (() => void)[] = [];

    // Apply hover animations to social icons
    const socialCleanups = socialIconRefs.current
      .filter(Boolean)
      .map((el) => animateSocialIcon(el as HTMLAnchorElement))
      .filter((cleanup): cleanup is () => void => cleanup !== undefined);
    cleanupFunctions.push(...socialCleanups);

    // Add floating animation to logo after it loads
    if (logoRef.current && logoLoaded) {
      const logoCleanup = createLogoFloatingAnimation(logoRef.current);
      if (logoCleanup) {
        cleanupFunctions.push(logoCleanup);
      }
    }

    // Clean up all animations on unmount
    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [logoLoaded]);

  return (
    <>
      <div className="mb-6">
        <div className="relative h-16 mb-4">
          {/* Loading indicator */}
          {!logoLoaded && !logoError && (
            <div className="absolute inset-0 flex items-center justify-start">
              <div className="w-8 h-8 border-4 border-cannabis-green-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Error fallback */}
          {logoError && (
            <div className="h-16 flex items-center">
              <div className="text-xl font-bold text-cannabis-green-300">
                {BUSINESS_NAME}
              </div>
            </div>
          )}

          {/* Logo image with optimized loading */}
          {!logoError && (
            <img
              ref={logoRef}
              src="/images/logo/street-lawyer-services-logo.png"
              alt={`${BUSINESS_NAME} Logo`}
              className="h-16 transition-transform duration-300"
              style={{ opacity: 0 }} // Start hidden for smooth fade-in
              loading="lazy"
              decoding="async"
              onLoad={(e) => handleLogoLoad(e.target as HTMLImageElement)}
              onError={handleLogoError}
            />
          )}
        </div>

        <p className="text-gray-400 leading-relaxed">{BUSINESS_DESCRIPTION}</p>
      </div>

      <div className="mt-6">
        <h4 className="font-semibold mb-4 text-cannabis-green-300 text-lg">
          Follow Us
        </h4>
        <div className="flex flex-wrap gap-4">
          {/* Social Links */}
          {socialMediaLinks.map((social, index) => {
            const IconComponent = getSocialIcon(social.platform);

            return (
              <a
                key={social.platform}
                href={social.url}
                ref={(el) => (socialIconRefs.current[index] = el)}
                className="text-white hover:text-cannabis-green-300 transition-colors duration-300 p-2 rounded-full hover:bg-cannabis-green-500/20"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
              >
                <IconComponent size={24} weight="fill" />
              </a>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default CompanyInfo;
